import React, { useState } from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  GlobalStyles,
} from '@mui/material';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import SurveyPreview from './SurveyPreview';

/**
 * SmartphonePreviewWrapper
 * Renders SurveyPreview inside a realistic smartphone frame, or in a plain
 * full-width "normal" view when the user switches modes.
 * Props:
 *  - survey: the survey object passed to SurveyPreview
 *  - deviceColor: 'black' | 'white'  (default: 'black')
 */
const SmartphonePreviewWrapper = ({ survey, deviceColor = 'black' }) => {
  const [activeDevice, setActiveDevice] = useState(deviceColor);
  const [viewMode, setViewMode] = useState('phone'); // 'phone' | 'normal'

  const COLORS = {
    black: {
      frame: '#1C1C1E',
      frameShine: '#3A3A3C',
      button: '#2C2C2E',
      label: 'Black',
    },
    white: {
      frame: '#E8E8ED',
      frameShine: '#FAFAFC',
      button: '#D1D1D6',
      label: 'White',
    },
  };

  const c = COLORS[activeDevice];

  // Tamaño y proporción de un smartphone moderno real (~19.5:9).
  // Se agranda un poco respecto al iPhone SE/13 mini (375x812) para dar
  // más aire al contenido dentro del marco, acercándose a un iPhone
  // 14/15 estándar (390-402 de ancho lógico).
  const PHONE_WIDTH = 500;
  const PHONE_HEIGHT = 950;

  const handleDeviceChange = (_e, value) => {
    if (value) setActiveDevice(value);
  };

  const handleViewModeChange = (_e, value) => {
    if (value) setViewMode(value);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'radial-gradient(ellipse at 30% 20%, #1a1a2e 0%, #0d0d1a 40%, #050510 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <GlobalStyles
        styles={{
          '@keyframes pulse-dot': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.4 },
          },

          /* ── Scrollbar ── */
          '.phone-screen::-webkit-scrollbar, .normal-screen::-webkit-scrollbar': {
            width: 3,
          },
          '.phone-screen::-webkit-scrollbar-track, .normal-screen::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '.phone-screen::-webkit-scrollbar-thumb, .normal-screen::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.15)',
            borderRadius: 3,
          },

          /* ── Make content fill ~96% of the phone screen width ── */
          '.phone-screen > *': {
            width: '100% !important',
            boxSizing: 'border-box !important',
          },
          '.phone-screen .MuiContainer-root': {
            width: '100% !important',
            maxWidth: '100% !important',
            paddingLeft: '10px !important',
            paddingRight: '10px !important',
            boxSizing: 'border-box !important',
          },
          '.phone-screen .MuiCard-root, .phone-screen .MuiPaper-root': {
            width: '100% !important',
            boxSizing: 'border-box !important',
            marginLeft: '0 !important',
            marginRight: '0 !important',
            marginBottom: '10px !important',
            borderRadius: '10px !important',
          },
          '.phone-screen .MuiCardContent-root': {
            padding: '16px 16px 12px !important',
          },

          /* Tamaños de tipografía pensados para leerse bien dentro del
             marco, ligeramente más generosos ahora que el marco creció */
          '.phone-screen .MuiTypography-h4': { fontSize: '1.55rem !important' },
          '.phone-screen .MuiTypography-h5': { fontSize: '1.3rem !important' },
          '.phone-screen .MuiTypography-h6': { fontSize: '1.1rem !important' },
          '.phone-screen .MuiTypography-body1': { fontSize: '0.97rem !important' },
          '.phone-screen .MuiTypography-body2': { fontSize: '0.87rem !important' },
          '.phone-screen .MuiTypography-subtitle1': { fontSize: '0.97rem !important' },
          '.phone-screen .MuiTypography-caption': { fontSize: '0.76rem !important' },

          '.phone-screen .MuiButton-root': {
            fontSize: '0.92rem !important',
            padding: '9px 18px !important',
          },

          '.phone-screen .MuiButtonGroup-root, .phone-screen .MuiButton-fullWidth': {
            width: '100% !important',
          },

          '.phone-screen .MuiRating-root svg': { fontSize: '2.05rem !important' },

          '.phone-screen .MuiFormControl-root, .phone-screen .MuiTextField-root, .phone-screen .MuiSelect-root': {
            width: '100% !important',
          },

          '.phone-screen .MuiGrid-root': {
            width: '100% !important',
            margin: '0 !important',
          },
          '.phone-screen .MuiGrid-item': {
            paddingLeft: '6px !important',
            paddingRight: '6px !important',
            boxSizing: 'border-box !important',
          },

          '.phone-screen .MuiSlider-root': {
            width: 'calc(100% - 16px) !important',
            margin: '0 8px !important',
          },

          '.phone-screen .MuiChip-root': { fontSize: '0.8rem !important' },

          '.phone-screen .MuiDivider-root': {
            marginLeft: '0 !important',
            marginRight: '0 !important',
          },

          /*
           * SurveyResponse.css define varios tamaños con clamp(min, Xvw, max),
           * donde "vw" se mide contra el ancho de TODA la ventana del
           * navegador, no contra este marco. Convertimos ese contenedor en
           * un "container query" propio y re-declaramos esas variables en
           * cqw (relativo SOLO al ancho de este marco), sin tocar el CSS
           * compartido con la encuesta real a pantalla completa.
           */
          '.phone-screen': {
            containerType: 'inline-size',
            containerName: 'phone-frame',
            '--sp-xs': 'clamp(0.3rem, 1.6cqw, 0.5rem)',
            '--sp-sm': 'clamp(0.5rem, 2.4cqw, 1rem)',
            '--sp-md': 'clamp(0.75rem, 3.2cqw, 1.5rem)',
            '--sp-lg': 'clamp(1rem, 4cqw, 1.75rem)',
            '--sp-xl': 'clamp(1.25rem, 5cqw, 2rem)',
            '--sp-2xl': 'clamp(1.5rem, 6cqw, 2.5rem)',
            '--progress-bar-height': 'clamp(48px, 13cqw, 60px)',
          },

          '.phone-screen .rating, .phone-screen .star-icon': {
            fontSize: 'clamp(1.85rem, 8.5cqw, 2.5rem) !important',
          },
          '.phone-screen .emoji-label': {
            fontSize: 'clamp(0.7rem, 2.2cqw, 0.82rem) !important',
          },
          '.phone-screen .question-text-radio': {
            fontSize: 'clamp(1.02rem, 3.8cqw, 1.25rem) !important',
          },

          /* Normal mode: no container-query overrides, deja que
             SurveyResponse.css use su clamp(...) tal cual, basado en el
             viewport real — el objetivo de este modo es ver la encuesta
             como se ve en un navegador de escritorio normal. */
          '.normal-screen .MuiContainer-root': {
            paddingLeft: '24px !important',
            paddingRight: '24px !important',
          },
        }}
      />

      {/* bg decoration */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '-10%',
          left: '-10%',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(100,60,200,0.18) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-10%',
          right: '-10%',
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(30,150,255,0.12) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* Mode switcher: Phone / Normal */}
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={handleViewModeChange}
        sx={{
          position: 'relative',
          zIndex: 10,
          mb: 2.5,
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 24,
          padding: '4px',
          '& .MuiToggleButton-root': {
            border: 'none',
            borderRadius: 20,
            color: 'rgba(255,255,255,0.65)',
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: 0.2,
            textTransform: 'none',
            padding: '8px 20px',
            gap: 0.75,
            '&.Mui-selected': {
              background: '#4FC3F7',
              color: '#0d0d1a',
              '&:hover': { background: '#4FC3F7' },
            },
          },
        }}
      >
        <ToggleButton value="phone">
          <PhoneIphoneIcon fontSize="small" sx={{ mr: 0.75 }} />
          Phone
        </ToggleButton>
        <ToggleButton value="normal">
          <DesktopWindowsIcon fontSize="small" sx={{ mr: 0.75 }} />
          Normal
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Color picker: solo aplica al marco del teléfono */}
      {viewMode === 'phone' && (
        <Box
          sx={{
            display: 'flex',
            gap: 1.25,
            justifyContent: 'center',
            mb: 3.5,
            position: 'relative',
            zIndex: 10,
          }}
        >
          {Object.keys(COLORS).map((key) => (
            <Box
              key={key}
              onClick={() => setActiveDevice(key)}
              title={COLORS[key].label}
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: COLORS[key].frame,
                cursor: 'pointer',
                transition: 'transform 0.2s, border-color 0.2s',
                border:
                  activeDevice === key
                    ? '2px solid #4FC3F7'
                    : '2px solid rgba(255,255,255,0.2)',
                transform: activeDevice === key ? 'scale(1.2)' : 'scale(1)',
                boxShadow: activeDevice === key ? '0 0 10px rgba(79,195,247,0.5)' : 'none',
              }}
            />
          ))}
        </Box>
      )}

      {viewMode === 'phone' ? (
        /* Phone frame */
        <Box
          sx={{
            position: 'relative',
            zIndex: 10,
            width: PHONE_WIDTH,
            height: PHONE_HEIGHT,
            borderRadius: '48px',
            background: `linear-gradient(145deg, ${c.frameShine} 0%, ${c.frame} 60%)`,
            boxShadow: `
              0 0 0 1px rgba(255,255,255,0.08),
              0 40px 80px rgba(0,0,0,0.7),
              0 20px 40px rgba(0,0,0,0.5),
              inset 0 1px 0 rgba(255,255,255,0.15),
              inset 0 -1px 0 rgba(0,0,0,0.3)
            `,
            padding: '2px',
            flexShrink: 0,
          }}
        >
          {/* Physical buttons */}
          <Box
            title="Silent switch"
            sx={{
              position: 'absolute',
              left: -4,
              top: 131,
              width: 4,
              height: 30,
              borderRadius: '2px 0 0 2px',
              background: `linear-gradient(180deg, ${c.frameShine} 0%, ${c.button} 100%)`,
              boxShadow: '-1px 0 3px rgba(0,0,0,0.4)',
            }}
          />
          <Box
            title="Vol +"
            sx={{
              position: 'absolute',
              left: -4,
              top: 176,
              width: 4,
              height: 56,
              borderRadius: '2px 0 0 2px',
              background: `linear-gradient(180deg, ${c.frameShine} 0%, ${c.button} 100%)`,
              boxShadow: '-1px 0 3px rgba(0,0,0,0.4)',
            }}
          />
          <Box
            title="Vol -"
            sx={{
              position: 'absolute',
              left: -4,
              top: 241,
              width: 4,
              height: 56,
              borderRadius: '2px 0 0 2px',
              background: `linear-gradient(180deg, ${c.frameShine} 0%, ${c.button} 100%)`,
              boxShadow: '-1px 0 3px rgba(0,0,0,0.4)',
            }}
          />
          <Box
            title="Power"
            sx={{
              position: 'absolute',
              right: -4,
              top: 187,
              width: 4,
              height: 68,
              borderRadius: '0 2px 2px 0',
              background: `linear-gradient(180deg, ${c.frameShine} 0%, ${c.button} 100%)`,
              boxShadow: '1px 0 3px rgba(0,0,0,0.4)',
            }}
          />

          {/* Inner bezel */}
          <Box
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: '46px',
              background: '#000',
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Glass reflection */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '35%',
                borderRadius: '46px 46px 0 0',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, transparent 100%)',
                pointerEvents: 'none',
                zIndex: 5,
              }}
            />

            {/* Camera cutout genérico (sin marca de dispositivo) */}
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 110,
                height: 32,
                background: '#000',
                borderRadius: '16px',
                zIndex: 20,
                boxShadow: '0 0 0 1px rgba(255,255,255,0.06)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '14px',
              }}
            >
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, #2a2a30 0%, #141416 100%)',
                  boxShadow: '0 0 4px rgba(255,255,255,0.05) inset',
                }}
              />
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, #2a2a30 0%, #141416 100%)',
                  boxShadow: '0 0 4px rgba(255,255,255,0.05) inset',
                }}
              />
            </Box>

            {/* Scrollable screen content */}
            <Box
              className="phone-screen"
              sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                paddingTop: '58px',
                paddingBottom: '16px',
                paddingLeft: 0,
                paddingRight: 0,
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(255,255,255,0.15) transparent',
                background: '#fff',
                boxSizing: 'border-box',
              }}
            >
              <SurveyPreview survey={survey} />
            </Box>

            {/* Home indicator */}
            <Box
              sx={{
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#fff',
                flexShrink: 0,
              }}
            >
              <Box sx={{ width: 128, height: 5, borderRadius: '3px', background: 'rgba(0,0,0,0.18)' }} />
            </Box>
          </Box>
        </Box>
      ) : (
        /* Normal (desktop-like) window frame */
        <Box
          sx={{
            position: 'relative',
            zIndex: 10,
            width: '100%',
            maxWidth: 900,
            borderRadius: '16px',
            background: '#fff',
            boxShadow: `
              0 0 0 1px rgba(255,255,255,0.08),
              0 30px 60px rgba(0,0,0,0.55),
              0 10px 24px rgba(0,0,0,0.4)
            `,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              padding: '12px 16px',
              background: '#EDEDF0',
              borderBottom: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            <Box sx={{ width: 11, height: 11, borderRadius: '50%', background: '#FF5F57' }} />
            <Box sx={{ width: 11, height: 11, borderRadius: '50%', background: '#FEBC2E' }} />
            <Box sx={{ width: 11, height: 11, borderRadius: '50%', background: '#28C840' }} />
          </Box>
          <Box
            className="normal-screen"
            sx={{
              maxHeight: '78vh',
              overflowY: 'auto',
              overflowX: 'hidden',
              background: '#fff',
              boxSizing: 'border-box',
            }}
          >
            <SurveyPreview survey={survey} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SmartphonePreviewWrapper;

